import React, { useMemo } from "react";
import {
    Box,
    Paper,
    Typography,
    Table,
    TableHead,
    TableRow,
    TableCell,
    TableBody,
    CircularProgress,
} from "@mui/material";
import { BarChart } from "@mui/x-charts/BarChart";

export default function PearliRangeComparison({
    prevScores,
    focusScores,
    prevLabel,
    focusLabel,
    prevMonth,
    focusMonth,
    loading,
    hasPrevData = true,
    hasFocusData = true,
    isAssessment = false,
}) {
    const hasAnyData = hasPrevData || hasFocusData;

    // Domains
    const labels = [
        "Physical",
        "Emotional",
        "Acceptance",
        "Relationships",
        "Loving-Kindness",
        "Intelligence",
    ];

    // Convert month object → text ("Sep 2025") - for Pearli
    const formatMonth = (m) => {
        if (!m) return "Loading";
        const d = new Date(m.start);
        return d.toLocaleDateString("en-US", {
            month: "short",
            year: "numeric",
        });
    };

    // Use provided labels (Assessment) or format from month objects (Pearli)
    const prevMonthLabel = prevLabel || formatMonth(prevMonth);
    const focusMonthLabel = focusLabel || formatMonth(focusMonth);

    const prevData = [
        prevScores.physical,
        prevScores.emotional,
        prevScores.acceptance,
        prevScores.relationships,
        prevScores.loving_kindness || prevScores.lovingKindness,
        prevScores.intellectual || prevScores.intelligence,
    ];

    const focusData = [
        focusScores.physical,
        focusScores.emotional,
        focusScores.acceptance,
        focusScores.relationships,
        focusScores.loving_kindness || focusScores.lovingKindness,
        focusScores.intellectual || focusScores.intelligence,
    ];

    const comparisonRows = useMemo(() => {
        return labels.map((label, idx) => {
            const prevVal = prevData[idx];
            const curVal = focusData[idx];

            const diff =
                typeof prevVal === "number" && typeof curVal === "number"
                    ? curVal - prevVal
                    : null;

            const pct =
                typeof prevVal === "number" && prevVal !== 0
                    ? (diff / prevVal) * 100
                    : null;

            const color =
                diff > 0
                    ? "#19B880"
                    : diff < 0
                        ? "#EF4444"
                        : "#334155";

            return {
                label,
                prevVal,
                curVal,
                diff,
                pct,
                color,
            };
        });
    }, [prevData, focusData]);
    console.log(comparisonRows)

    return (
        <Box mt={4}>
            {/* Chart Card */}
            <Paper sx={{ p: 3, mb: 4 }}>
                <Typography variant="h6" fontWeight={700} mb={1}>
                    {isAssessment ? "Assessment" : "PEARLI Assessment"} Comparison ({prevMonthLabel} → {focusMonthLabel})
                </Typography>
                <Typography variant="body2" color="text.secondary" mb={2}>
                    Data reflects average {isAssessment ? "platform assessment" : "PEARLI assessment"} score trends over time
                </Typography>

                {loading ? (
                    <Box display="flex" justifyContent="center" py={5}>
                        <CircularProgress />
                    </Box>
                ) : !hasAnyData ? (
                    <Box
                        sx={{
                            height: 320,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            color: "text.secondary",
                            fontSize: "0.9rem",
                            opacity: 0.8,
                        }}
                    >
                        No assessment data available for the selected filters.
                    </Box>
                ) : (
                    <BarChart
                        xAxis={[
                            {
                                scaleType: "band",
                                data: labels,
                            },
                        ]}
                        yAxis={[{ label: "Average Score", width: 40 }]}
                        series={[
                            {
                                data: prevData,
                                color: "#3B82F6",
                                label: prevMonthLabel,
                            },
                            {
                                data: focusData,
                                color: "#0C8F63",
                                label: focusMonthLabel,
                            },
                        ]}
                        height={320}
                    />
                )}
            </Paper>

            {/* Table Card */}
            <Paper sx={{ p: 3 }}>
                <Typography variant="h6" fontWeight={700} mb={1}>
                    {isAssessment ? "Assessment" : "PEARLI Assessment"} Summary ({prevMonthLabel} → {focusMonthLabel})
                </Typography>
                <Typography variant="body2" color="text.secondary" mb={2}>
                    Showing change between selected months
                </Typography>

                {loading ? (
                    <Box display="flex" justifyContent="center" py={5}>
                        <CircularProgress />
                    </Box>
                ) : !hasAnyData ? (
                    <Box
                        sx={{
                            minHeight: 200,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            color: "text.secondary",
                            fontSize: "0.9rem",
                            opacity: 0.8,
                        }}
                    >
                        No assessment data available for the selected filters.
                    </Box>
                ) : (
                    <Table>
                        <TableHead>
                            <TableRow>
                                <TableCell>Domain</TableCell>
                                <TableCell>{prevMonthLabel}</TableCell>
                                <TableCell>{focusMonthLabel}</TableCell>
                                <TableCell>Change</TableCell>
                                <TableCell>Change (%)</TableCell>
                            </TableRow>
                        </TableHead>

                        <TableBody>
                            {comparisonRows.map((row) => (
                                <TableRow key={row.label}>
                                    <TableCell>{row.label}</TableCell>

                                    <TableCell>
                                        {typeof row.prevVal === "number"
                                            ? `${row.prevVal.toFixed(1)}`
                                            : "—"}
                                    </TableCell>

                                    <TableCell>
                                        {typeof row.curVal === "number"
                                            ? `${row.curVal.toFixed(1)}`
                                            : "—"}
                                    </TableCell>

                                    <TableCell sx={{ color: row.color, fontWeight: 600 }}>
                                        {row.diff !== null
                                            ? row.diff.toFixed(1)
                                            : "N/A"}
                                    </TableCell>

                                    <TableCell sx={{ color: row.color, fontWeight: 600 }}>
                                        {row.pct !== null
                                            ? `${row.pct.toFixed(1)}%`
                                            : "N/A"}
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                )}
            </Paper>
        </Box>
    );
}
